'use client';

import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Type,
  AlignLeft,
  Hash,
  Calendar,
  List,
  CheckSquare,
  Phone,
  AtSign,
  Star,
  ClipboardList,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { dynamicFormQueries } from '../services/dynamic-forms.queries';
import type { DynamicForm, DynamicFieldType, DynamicFormType } from '../types';

interface DynamicFormPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: DynamicForm | null;
}

const FIELD_TYPE_ICONS: Record<
  DynamicFieldType,
  React.ComponentType<{ className?: string }>
> = {
  short_text: Type,
  long_text: AlignLeft,
  number: Hash,
  date: Calendar,
  selection_list: List,
  yes_no: CheckSquare,
  phone: Phone,
  email: AtSign
};

const FIELD_TYPE_KEYS: Record<DynamicFieldType, string> = {
  short_text: 'fieldShortText',
  long_text: 'fieldLongText',
  number: 'fieldNumber',
  date: 'fieldDate',
  selection_list: 'fieldSelectionList',
  yes_no: 'fieldYesNo',
  phone: 'fieldPhone',
  email: 'fieldEmail'
};

const TYPE_KEYS: Record<DynamicFormType, string> = {
  registration: 'typeRegistration',
  filling: 'typeFilling'
};

export function DynamicFormPreviewDialog({
  open,
  onOpenChange,
  form
}: DynamicFormPreviewDialogProps) {
  const t = useTranslations('dynamicForms');

  const { data: detailData, isLoading } = useQuery({
    ...dynamicFormQueries.detail(form?.id ?? 0),
    enabled: open && !!form?.id
  });

  const fullForm = detailData?.data ?? form;
  const fields = fullForm?.fields ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle className='text-xl'>{fullForm?.name}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
          </div>
        ) : fullForm ? (
          <div className='space-y-5'>
            {/* Metadata */}
            <div className='space-y-3'>
              <div className='flex flex-wrap items-center gap-2'>
                <Badge
                  variant='default'
                  className={cn(
                    fullForm.status === 'active'
                      ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
                      : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
                  )}
                >
                  {fullForm.status === 'active'
                    ? t('statusActive')
                    : t('statusDraft')}
                </Badge>
                <Badge variant='outline' className='gap-1'>
                  <ClipboardList className='h-3 w-3' />
                  {t(TYPE_KEYS[fullForm.type])}
                </Badge>
                {fullForm.is_default && (
                  <Badge
                    variant='outline'
                    className='gap-1 border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400'
                  >
                    <Star className='h-3 w-3 fill-current' />
                    {t('setDefault')}
                  </Badge>
                )}
              </div>

              <div className='text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm'>
                <span>{fullForm.form_id_mask}</span>
                <span>&middot;</span>
                <span>v{fullForm.version}</span>
                <span>&middot;</span>
                <span>
                  {fields.length} {t('fields')}
                </span>
              </div>

              {fullForm.description && (
                <p className='text-muted-foreground text-sm'>
                  {fullForm.description}
                </p>
              )}
            </div>

            <Separator />

            {/* Fields */}
            {fields.length === 0 ? (
              <p className='text-muted-foreground py-8 text-center text-sm'>
                {t('noFieldsAdded')}
              </p>
            ) : (
              <div className='space-y-2'>
                {[...fields]
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((field, idx) => {
                    const Icon = FIELD_TYPE_ICONS[field.type];
                    const options = field.options ?? [];

                    return (
                      <div
                        key={field.id}
                        className='bg-muted/40 rounded-lg border px-4 py-3'
                      >
                        <div className='flex items-center gap-3'>
                          <span className='text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-current/10 text-xs font-medium'>
                            {idx + 1}
                          </span>
                          <div className='min-w-0 flex-1'>
                            <div className='flex items-center gap-2'>
                              <span className='truncate font-medium'>
                                {field.label}
                              </span>
                              {field.is_required && (
                                <Badge
                                  variant='secondary'
                                  className='h-5 px-1.5 text-[10px]'
                                >
                                  {t('fieldRequired')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Badge
                            variant='outline'
                            className='shrink-0 gap-1 text-xs'
                          >
                            <Icon className='h-3 w-3' />
                            {t(FIELD_TYPE_KEYS[field.type])}
                          </Badge>
                        </div>

                        {field.type === 'selection_list' &&
                          options.length > 0 && (
                            <div className='mt-2 ps-9'>
                              <div className='flex flex-wrap gap-1.5'>
                                {options
                                  .filter(
                                    (o) => typeof o === 'string' && o.trim()
                                  )
                                  .map((option, i) => (
                                    <Badge
                                      key={i}
                                      variant='secondary'
                                      className={cn(
                                        'text-xs',
                                        field.default_value === option &&
                                          'border-primary/50 bg-primary/10 text-primary'
                                      )}
                                    >
                                      {option}
                                      {field.default_value === option && (
                                        <span className='ms-1 opacity-60'>
                                          ({t('defaultOption')})
                                        </span>
                                      )}
                                    </Badge>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
